import { calculatePercentChange, safeDivide } from 'src/utils/app';
import { weekIdToTimestamp } from 'src/utils/dateUtils';
import { useAppSelector, useMainTimePeriod } from 'src/utils/Hooks';

const getFilteredRangeData = ({
  data,
  startWeek,
  endWeek
}: {
  data: [number, number][];
  startWeek: number;
  endWeek: number;
}) => {
  if (startWeek === endWeek) {
    return data.filter(([weekId]) => {
      const weekIdNum = Number(weekId);
      return weekIdNum === startWeek;
    });
  }

  return data.filter(([weekId]) => {
    const weekIdNum = Number(weekId);
    return weekIdNum >= startWeek && weekIdNum <= endWeek;
  });
};

const getAverage = (data: [number, number][]) => {
  return safeDivide(
    data.reduce((acc, [_, value]) => acc + value, 0),
    data.length
  );
};

const sumFilteredRangeData = (data: [number, number][], startWeek: number, endWeek: number) =>
  getFilteredRangeData({ data, startWeek, endWeek }).reduce((acc, [_, value]) => acc + value, 0);

const calculateDerivedMetricAverage = ({
  numeratorData,
  denominatorData,
  startWeek,
  endWeek
}: {
  numeratorData: [number, number][];
  denominatorData: [number, number][];
  startWeek: number;
  endWeek: number;
}) => {
  const numeratorSum = sumFilteredRangeData(numeratorData, startWeek, endWeek);
  const denominatorSum = sumFilteredRangeData(denominatorData, startWeek, endWeek);
  return safeDivide(numeratorSum, denominatorSum);
};

/**
 *
 * Instead of fetching data from in the hook, we are passing in the data from the server proxy, (format will be [weekId, value]) to get requiredData for the chart
 */
export const useChartMetricByWeekId = ({
  primaryRawData,
  secondaryRawData,
  primaryDerivedMetricRawData,
  secondaryDerivedMetricRawData
}: {
  primaryRawData: [number, number][];
  secondaryRawData: [number, number][];
  primaryDerivedMetricRawData: {
    dataSource1: [number, number][];
    dataSource2: [number, number][];
  };
  secondaryDerivedMetricRawData: {
    dataSource1: [number, number][];
    dataSource2: [number, number][];
  };
}) => {
  const { startWeek, endWeek, id: periodId } = useMainTimePeriod();
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const adjustedStartWeek = periodId === '1w' ? endWeek - 1 : startWeek;
  const adjustedEndWeek = endWeek;

  // For 1-week periods, comparison should use only the latest week (endWeek) instead of the range
  const adjustedComparisonStartWeek = periodId === '1w' ? comparisonEndWeek : comparisonStartWeek;
  const adjustedComparisonEndWeek = periodId === '1w' ? comparisonEndWeek : comparisonEndWeek;

  // Note: Prior Period merging is handled by SplineChartV2's useSplineChartV2Data hook
  // So we don't add the last comparison point here anymore to avoid double-merging
  const primarySeries = primaryRawData.map(([weekId, value]) => [weekIdToTimestamp(weekId), value]);

  const secondarySeries = secondaryRawData.map(([weekId, value]) => [weekIdToTimestamp(weekId), value]);

  const primaryAverage = getAverage(
    getFilteredRangeData({
      data: primaryRawData,
      startWeek: adjustedStartWeek,
      endWeek: adjustedEndWeek
    })
  );

  const comparisonAverage = getAverage(
    getFilteredRangeData({
      data: secondaryRawData,
      startWeek: adjustedComparisonStartWeek,
      endWeek: adjustedComparisonEndWeek
    })
  );

  // This value is not just average of the one [weekId, value][] values, it sums up all the values in dataSource1 and divides by the sum of all the values in dataSource2
  const primaryDerivedMetricAverage = calculateDerivedMetricAverage({
    numeratorData: primaryDerivedMetricRawData.dataSource1,
    denominatorData: primaryDerivedMetricRawData.dataSource2,
    startWeek: adjustedStartWeek,
    endWeek: adjustedEndWeek
  });

  const comparisonDerivedMetricAverage = calculateDerivedMetricAverage({
    numeratorData: secondaryDerivedMetricRawData.dataSource1,
    denominatorData: secondaryDerivedMetricRawData.dataSource2,
    startWeek: adjustedComparisonStartWeek,
    endWeek: adjustedComparisonEndWeek
  });


  const percentChange = calculatePercentChange(primaryAverage, comparisonAverage);
  const primaryDerivedMetricPercentChange = calculatePercentChange(
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage
  );

  const primaryLastWeekValue = primarySeries?.length ? primarySeries[primarySeries.length - 1][1] : 0;
  const comparisonLastWeekValue = (() => {
    if (!secondaryRawData?.length) return 0;
    if (periodId === '1w') {
      const match = secondaryRawData.find(([weekId]) => Number(weekId) === adjustedComparisonEndWeek);
      if (match) return match[1];
    }
    return secondarySeries[secondarySeries.length - 1][1];
  })();

  const primaryPercentChangeLastWeek = calculatePercentChange(primaryLastWeekValue, comparisonLastWeekValue);

  return {
    primarySeries,
    secondarySeries,
    primaryAverage,
    comparisonAverage,
    percentChange,
    primaryDerivedMetricPercentChange,
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage,
    primaryLastWeekValue,
    comparisonLastWeekValue,
    primaryPercentChangeLastWeek
  };
};
